import { Injectable } from '@angular/core';
import { of, timer } from 'rxjs';
import { delay, switchMap, take } from 'rxjs/operators';
import {
  HttpDownloadProgressEvent,
  HttpEventType,
  HttpResponse,
  HttpSentEvent,
  HttpUploadProgressEvent
} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ApiProgressUtilsService {

  constructor() {
  }

  /**
   * generate an upload progress observable, splitting the given time into 100 segments, and reporting after each inverval
   * @param progressTime total progress time in milliseconds
   * @param result result to be returned after the progress is done
   * @param measurementTime if provided, delay the result by this many additional milliseconds
   * @returns observable for the server response
   */
  generateUploadProgress<T>(progressTime: number, result?: T, measurementTime?: number) {
    return this.generateProgress<T>(HttpEventType.UploadProgress, progressTime, result, measurementTime);
  }

  /**
   * generate a download progress observable, splitting the given time into 100 segments, and reporting after each interval
   * @param progressTime total progress time in milliseconds
   * @param result result to be returned after the progress is done
   * @param measurementTime if provided, delay the result by this many additional milliseconds
   * @returns observable for the server response
   */
  generateDownloadProgress<T>(progressTime: number, result?: T, measurementTime?: number) {
    return this.generateProgress<T>(HttpEventType.DownloadProgress, progressTime, result, measurementTime);
  }

  /**
   * generate a generic progress observable, splitting the given time into 100 segments, and reporting after each interval
   * @param type type of the progress, represented as an integer
   * @param progressTime total progress time in milliseconds
   * @param result result to be returned after the progress is done
   * @param measurementTime if provided, delay the result by this many additional milliseconds
   * @returns observable for the server response
   */
  private generateProgress<T>(type: HttpEventType, progressTime: number, result?: T, measurementTime = 0) {

    const emits = result ? 101 : 100;

    const period = Math.floor(progressTime / 100) + 1;

    return timer(0, period).pipe(
      take(emits + 1),
      switchMap((count) => {
        // report the current progress every 'progressTime/100', i.e. split it into 100 steps
        const total = 100 * period;
        let loaded = count * period;

        if (count === 0 && result) {
          return of<HttpSentEvent>({ type: HttpEventType.Sent });
        }

        if (count <= 100) {

          if (loaded > total) {
            loaded = total - 1;
          }

          if (count === 100) {
            loaded = total;
          }

          if (type === HttpEventType.UploadProgress) {
            return of<HttpUploadProgressEvent>({
              loaded,
              total,
              type
            });
          } else if (type === HttpEventType.DownloadProgress) {
            return of<HttpDownloadProgressEvent>({
              loaded,
              total,
              type
            });
          } else {
            throw Error('Unknown eventtype for progress');
          }
        }
        // after 100 steps, return the original result, delayed by another given time if desired
        if (count === 101) {
          return of<HttpResponse<T>>(new HttpResponse<T>({ body: result })).pipe(delay(measurementTime));
        }
      }),
    );
  }
}
